import { useState, useEffect } from 'react';
import { Bell, Check, Loader2, Clock, BellOff, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import usePushNotifications from '../hooks/usePushNotifications';
import api from '../lib/api';

export default function PushNotificationPrompt() {
  const [showDialog, setShowDialog] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [preferenceLoaded, setPreferenceLoaded] = useState(false);
  
  const {
    permission,
    isSupported,
    isLoading,
    isSubscribed,
    subscribe,
    error
  } = usePushNotifications();

  // Log user action to audit and update backend preference
  const logNotificationAction = async (action, details = {}) => {
    try {
      await api.post('/push/log-action', { action, details });
    } catch (err) {
      console.error('Failed to log action:', err);
    }
  };

  // Check user's preference from backend on mount
  useEffect(() => {
    const checkPreference = async () => {
      try {
        const response = await api.get('/push/my-preference');
        if (response.data.success) {
          const { dismissed: isDismissed } = response.data.data;
          setDismissed(isDismissed);
          console.log('User preference loaded:', response.data.data);
        }
      } catch (err) {
        console.error('Failed to load preference:', err);
      } finally {
        setPreferenceLoaded(true);
      }
    };
    
    checkPreference();
  }, []);

  // Show prompt after login if not already subscribed or dismissed
  useEffect(() => {
    // Wait for preference to load
    if (!preferenceLoaded) return;

    // Debug logs
    console.log('PushNotificationPrompt useEffect:', {
      isSupported,
      permission,
      isSubscribed,
      dismissed,
      preferenceLoaded
    });

    if (dismissed) {
      console.log('Dialog hidden: user dismissed');
      return;
    }

    // Show dialog if browser supports push notifications
    // Even if permission is denied, we show dialog with instructions
    if (isSupported) {
      // Don't show if already subscribed
      if (isSubscribed) {
        console.log('Dialog hidden: already subscribed');
        return;
      }
      
      console.log('Showing dialog');
      const timer = setTimeout(() => {
        setShowDialog(true);
      }, 2000);
      
      return () => clearTimeout(timer);
    }
    
    console.log('Dialog hidden: not supported');
  }, [isSupported, permission, isSubscribed, dismissed, preferenceLoaded]);

  const handleEnable = async () => {
    const success = await subscribe();
    
    if (success) {
      await logNotificationAction('push_notification_enabled', { 
        action_type: 'enabled',
        browser_permission: 'granted'
      });
      toast.success('Bildirimler etkinleştirildi!', {
        description: 'Artık önemli güncellemelerden haberdar olacaksınız.'
      });
      // Backend'de tercih otomatik güncelleniyor (log-action içinde)
      setDismissed(false);
      setShowDialog(false);
    } else {
      await logNotificationAction('push_notification_failed', { 
        action_type: 'failed',
        error: error || 'Unknown error'
      });
      toast.error('Bildirim etkinleştirilemedi', {
        description: error || 'Tarayıcı izni verilemedi'
      });
    }
  };

  const handleDismiss = async () => {
    await logNotificationAction('push_notification_dismissed', { 
      action_type: 'never_ask_again'
    });
    // Backend'de tercih otomatik güncelleniyor (log-action içinde)
    setShowDialog(false);
    setDismissed(true);
    toast.info('Bildirimler kapatıldı', {
      description: 'Ayarlardan tekrar etkinleştirebilirsiniz.'
    });
  };

  const handleRemindLater = async () => {
    await logNotificationAction('push_notification_remind_later', { 
      action_type: 'remind_later'
    });
    // Backend'de tercih otomatik güncelleniyor (log-action içinde)
    setShowDialog(false);
    toast.info('Daha sonra hatırlatılacak');
  };

  // Don't render if preference not loaded yet or conditions not met
  if (!preferenceLoaded || !isSupported || dismissed || isSubscribed) {
    return null;
  }

  // Check if permission is denied
  const isPermissionDenied = permission === 'denied';

  return (
    <Dialog open={showDialog} onOpenChange={setShowDialog}>
      <DialogContent className="max-w-md w-[95vw] sm:w-full p-0 gap-0 overflow-hidden rounded-2xl">
        {/* Header with gradient background */}
        <div className={`p-6 text-white ${isPermissionDenied ? 'bg-gradient-to-br from-orange-500 to-red-500' : 'bg-gradient-to-br from-blue-500 to-blue-600'}`}>
          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
              {isPermissionDenied ? (
                <BellOff className="h-8 w-8 text-white" />
              ) : (
                <Bell className="h-8 w-8 text-white" />
              )}
            </div>
          </div>
          <DialogHeader className="space-y-2">
            <DialogTitle className="text-center text-xl font-semibold text-white">
              {isPermissionDenied ? 'Bildirimler Engellendi' : 'Bildirimleri Etkinleştir'}
            </DialogTitle>
            <DialogDescription className="text-center text-white/80 text-sm leading-relaxed">
              {isPermissionDenied 
                ? 'Tarayıcınızda bildirimler engellenmiş. Etkinleştirmek için tarayıcı ayarlarını değiştirmeniz gerekiyor.'
                : 'Önemli güncellemeler ve duyurulardan anında haberdar olun'}
            </DialogDescription>
          </DialogHeader>
        </div>
        
        {isPermissionDenied ? (
          /* Permission denied instructions */
          <div className="p-5">
            <div className="space-y-3">
              <div className="flex items-start gap-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-100 dark:border-orange-800">
                <div className="w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-orange-600 dark:text-orange-400 text-xs font-bold">1</span>
                </div>
                <span className="text-sm text-slate-700 dark:text-slate-300">Tarayıcı adres çubuğundaki kilit/bilgi simgesine tıklayın</span>
              </div>
              <div className="flex items-start gap-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-100 dark:border-orange-800">
                <div className="w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-orange-600 dark:text-orange-400 text-xs font-bold">2</span>
                </div>
                <span className="text-sm text-slate-700 dark:text-slate-300">"Site ayarları" veya "İzinler" seçeneğini açın</span>
              </div>
              <div className="flex items-start gap-3 p-3 bg-orange-50 dark:bg-orange-900/20 rounded-xl border border-orange-100 dark:border-orange-800">
                <div className="w-6 h-6 rounded-full bg-orange-100 dark:bg-orange-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-orange-600 dark:text-orange-400 text-xs font-bold">3</span>
                </div>
                <span className="text-sm text-slate-700 dark:text-slate-300">"Bildirimler" ayarını "İzin ver" olarak değiştirin</span>
              </div>
            </div>
            <div className="mt-4 p-3 bg-slate-100 dark:bg-slate-800 rounded-xl">
              <p className="text-xs text-slate-500 dark:text-slate-400 text-center">
                Ayarları değiştirdikten sonra sayfayı yenileyin
              </p>
            </div>
          </div>
        ) : (
          /* Normal features list */
          <div className="p-5">
            <div className="space-y-3">
              <div className="flex items-center gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-800">
                <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center flex-shrink-0">
                  <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
                </div>
                <span className="text-sm text-slate-700 dark:text-slate-300">İzin talepleri ve onayları</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-100 dark:border-blue-800">
                <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center flex-shrink-0">
                  <Check className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                <span className="text-sm text-slate-700 dark:text-slate-300">Önemli sistem bildirimleri</span>
              </div>
              <div className="flex items-center gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 rounded-xl border border-purple-100 dark:border-purple-800">
                <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-800 flex items-center justify-center flex-shrink-0">
                  <Check className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <span className="text-sm text-slate-700 dark:text-slate-300">Yönetici duyuruları</span>
              </div>
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="p-5 pt-0 space-y-2">
          {isPermissionDenied ? (
            <>
              <Button
                onClick={() => window.location.reload()}
                className="w-full h-12 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-medium"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Sayfayı Yenile
              </Button>
              <Button
                variant="ghost"
                onClick={handleDismiss}
                className="w-full h-10 rounded-xl text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              >
                <BellOff className="h-4 w-4 mr-2" />
                Bildirimleri İstemiyorum
              </Button>
            </>
          ) : (
            <>
              <Button
                onClick={handleEnable}
                disabled={isLoading}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-medium"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Etkinleştiriliyor...
                  </>
                ) : (
                  <>
                    <Bell className="h-4 w-4 mr-2" />
                    Bildirimleri Etkinleştir
                  </>
                )}
              </Button>
              
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  onClick={handleRemindLater}
                  className="flex-1 h-10 rounded-xl text-slate-600 dark:text-slate-400"
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Sonra
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleDismiss}
                  className="flex-1 h-10 rounded-xl text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                >
                  <BellOff className="h-4 w-4 mr-2" />
                  Hayır
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
